import React, { useState } from 'react';
import { ImageIcon, Loader2, X, AlertTriangle } from 'lucide-react';
import { api, uploadDownloadUrl, getApiErrorMessage } from '../../api/client';

/**
 * Opens one receipt photo full-screen, by `uploads.id`.
 *
 * The image is loaded through `/uploads/download/:id?dt=<token>` — the
 * authenticated proxy, never a presigned R2 URL held in state. Presigned URLs
 * expire and a stale one renders as a broken image that looks like a missing
 * receipt; the proxy mints a fresh redirect on every open. The token lives
 * ≤120s, so it is minted on tap rather than on mount — a list of forty
 * expenses would otherwise fire forty token requests for images nobody opened.
 *
 * Three states, never two: no receipt on the row (say so), the token or the
 * image failed (say so), the photo (show it). A row with no receipt id renders
 * the first, not a button that does nothing.
 */
export default function ReceiptViewer({
  uploadId,
  label = 'View receipt',
  className = '',
}: {
  uploadId: string | null | undefined;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!uploadId) {
    return <span className={`text-xs text-fieldiq-muted ${className}`}>No receipt</span>;
  }

  const show = async () => {
    setOpen(true);
    setError(null);
    setSrc(null);
    setLoading(true);
    try {
      const res = await api.uploads.downloadToken(uploadId);
      const token = res.data?.data?.token;
      if (!token) throw new Error('No download token returned');
      setSrc(uploadDownloadUrl(uploadId, token));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load the receipt.'));
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setSrc(null);
    setLoading(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={show}
        className={`inline-flex items-center gap-1.5 text-xs font-medium text-fieldiq-accent hover:underline ${className}`}
      >
        <ImageIcon size={14} /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-label="Receipt photo"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close receipt"
            className="absolute top-4 right-4 text-white hover:text-fieldiq-accent transition-colors z-10"
          >
            <X size={28} />
          </button>

          {error ? (
            <div
              className="bg-fieldiq-card border border-fieldiq-border rounded-xl px-5 py-4 text-sm text-fieldiq-danger flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <AlertTriangle size={16} /> {error}
            </div>
          ) : (
            <>
              {loading && (
                <div className="absolute text-fieldiq-accent flex items-center gap-2 text-sm">
                  <Loader2 size={20} className="animate-spin" /> Loading receipt…
                </div>
              )}
              {src && (
                <img
                  src={src}
                  alt="Receipt"
                  className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
                  onClick={(e) => e.stopPropagation()}
                  onLoad={() => setLoading(false)}
                  onError={() => {
                    setLoading(false);
                    setError('The receipt image could not be loaded.');
                  }}
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
